import { getErrorMsg } from 'app/core/errors/error-utils';
import { RootState } from 'app/core/redux/configure-store';
import { hasNoParams, Opt } from 'app/tools/general.types';
import {
  useEffectOnce,
  useEffectOnChanges,
} from 'app/tools/hooks/misc-hooks';
import { useObservableState } from 'observable-hooks';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  finalize,
  switchMap,
  tap,
} from 'rxjs/operators';
import { ApiError, isKeyedAxiosError } from '../api-core.types';
import { ApiSlice } from './api-slice-factory';

type ResourceOpts = {
  fresh?: boolean;
  noAutoFetch?: boolean;
};
type ResourceWithDataOpts = {
  fresh?: false;
  noAutoFetch?: true;
};

type ResourceHook<T, D> = (
  opts?: D extends void ? ResourceOpts : ResourceWithDataOpts
) => {
  resource: T;
  request: (data: D) => void;
  loading: boolean;
  loadingMissing: boolean;
  error: Opt<ApiError>;
};

type ApiState = RootState['api'];
type Names = keyof ApiState;
type Value<N extends Names> = ApiState[N]['value'];

const apiResourceHookFactory = <N extends Names, D = void>(
  slice: ApiSlice<Value<N>, N>,
  source$: (data: D) => Observable<Value<N>>
): ResourceHook<Value<N>, D> => {
  const { actions, name } = slice;

  const useResource = ({ fresh, noAutoFetch }: ResourceOpts = {}) => {
    const dispatch = useDispatch();

    const apiStore = useSelector(state => state.api[name]) || {};
    const appOnline = useSelector(s => s.context.appOnline);
    const autoFetch = !noAutoFetch && hasNoParams(source$);

    const [initialised, setInitialised] = useState(!fresh);
    const [loadInitialised, setLoadInitialised] = useState(
      !autoFetch
    );

    useEffect(() => {
      if (apiStore.value === undefined && !initialised) {
        setInitialised(true);
      }
    }, [apiStore.value, initialised]);

    useEffectOnce(() => {
      if (fresh) dispatch(actions.set(undefined));
    });

    const resource = initialised ? apiStore.value : undefined;
    const loading = loadInitialised ? !!apiStore.loading : true;

    const [, request] = useObservableState<Value<N>, D>(
      requestEvents$ =>
        requestEvents$.pipe(
          // exhaustMap = block incoming if any still in progress
          exhaustMap(d =>
            of(d).pipe(
              tap(() => dispatch(actions.start())),
              tap(() => dispatch(actions.clearErrors())),
              tap(() => {
                if (!loadInitialised) setLoadInitialised(true);
              }),
              switchMap(source$),
              tap(v => dispatch(actions.success<Value<N>>(v))),
              catchError((e: unknown) => {
                const errorRtn = isKeyedAxiosError(e)
                  ? e.data
                  : ({ detail: getErrorMsg(e) } as ApiError);

                dispatch(actions.failure(errorRtn));
                // failure -> okay to leave last fetch result in redux
                return EMPTY;
              }),
              finalize(() => dispatch(actions.end()))
            )
          )
        )
    );

    // NOTE: this useEffect auto-requests the resource if possible,
    // when it is needed to be retrieved or reloaded.
    useEffectOnce(() => {
      if (autoFetch) {
        // NOTE: if autoFetch, then 'source$' has no params, and so D
        // must be void (aka // request has no params). The compiler
        // just doesn't know this.
        (request as () => void)();
      }
    });
    useEffectOnChanges(() => {
      if (autoFetch && appOnline === 'backOnline') {
        (request as () => void)();
      }
    }, [appOnline, autoFetch, request]);

    return useMemo(
      () => ({
        resource,
        request,
        loading,
        loadingMissing: loading && !apiStore.value,
        error: apiStore.error,
      }),
      [apiStore.error, apiStore.value, loading, request, resource]
    );
  };

  return useResource;
};

export default apiResourceHookFactory;
